import { memo, useCallback } from 'react';
import Modal from 'react-modal';
import { useTranslator } from '@picsart/rc/services/localization';
import SvgIcon from '@picsart/rc/old/SvgIcon';
import { Button, ButtonSizeMD, ButtonVariants, ButtonSkinPremium } from '@picsart/button';
import IconCrownFilled from '@picsart/icons/IconCrownFilled';
import IconTickLarge from '@picsart/icons/IconTickLarge';

import Text from 'components/common/Text';
import dynamicTestId from 'utils/common/dynamicTestId';

import useStyles from './styles';
import testIds from './testIds';
import { IConfirmModal, confirmModalTypes } from './types';

const { closeModal, secondChoiceAction, firstChoiceAction } = confirmModalTypes;

const ConfirmModal = ({
  show,
  description,
  onAction,
  title = 'Are you sure?',
  secondChoice = 'No',
  firstChoice = 'Yes',
  shouldCloseOnOverlayClick = true,
  showCloseIcon = false,
  showUpgradeIcon = false,
  showTickIcon = false,
  hideFirstChoice = false,
  isPremiumButton = false,
  isDisabledSecondChoice = false,
}: IConfirmModal) => {
  const classes = useStyles();
  const translate = useTranslator();

  const onRequestClose = useCallback(() => {
    onAction(closeModal);
  }, [onAction]);

  const onFirstChoiceClick = useCallback(() => {
    onAction(firstChoiceAction);
  }, [onAction]);

  const onSecondChoiceClick = useCallback(() => {
    onAction(secondChoiceAction);
  }, [onAction]);

  return (
    <Modal
      isOpen={show}
      ariaHideApp={false}
      className={classes.confirmModal}
      overlayClassName={classes.confirmModalOverlay}
      onRequestClose={onRequestClose}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      testId={testIds.modal}
    >
      {showCloseIcon && <SvgIcon name="close-small-gen" className={classes.close} onClick={onRequestClose} data-testid={testIds.closeIcon} />}
      <Text size="large" tagType="h3" weight="bold" data-testid={testIds.modalTitle}>
        {translate(title)}
      </Text>
      <Text size="base16" color="secondary" tagType="p" data-test="modal root" data-testid={testIds.modalDescription}>
        {typeof description === 'string' ? translate(description) : description}
      </Text>
      <div className={classes.confirmModalChoice}>
        {!hideFirstChoice && (
          <Button
            size={ButtonSizeMD}
            variant={ButtonVariants.Outline}
            {... isPremiumButton && ({ skin: ButtonSkinPremium })}
            onClick={onFirstChoiceClick}
            dataTestId={dynamicTestId(testIds.confirmButton, firstChoice)}
          >
            {translate(firstChoice)}
          </Button>
        )}
        <Button
          size={ButtonSizeMD}
          onClick={onSecondChoiceClick}
          {... isPremiumButton && ({ skin: ButtonSkinPremium })}
          dataTestId={dynamicTestId(testIds.confirmButton, secondChoice)}
          {...showUpgradeIcon && ({ startIcon: IconCrownFilled })}
          {...showTickIcon && ({ endIcon: IconTickLarge })}
          {...isDisabledSecondChoice && ({ isDisabled: true })}
        >
          {translate(secondChoice)}
        </Button>
      </div>
    </Modal>
  );
};

export default memo(ConfirmModal);
